import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { MarketPnlResponse } from "../api/types";

/**
 * Счёт своей позиции по окну: реальные сделки и пробные наливы рядом.
 *
 * Опрос REST, а не сокет: потока по счёту нет вовсе, а собирается он из двух
 * таблиц и кеша исходов — то есть это в точности та форма, для которой в
 * проекте уже применяется TanStack Query (тот же довод, что у usePositions).
 *
 * Такт тот же, что у позиций (3 с), и это не совпадение: числа на панели
 * оператор сверяет со строкой позиции в быстрых действиях, и разная скорость их
 * обновления читалась бы как расхождение счёта.
 *
 * В ФОНОВОЙ вкладке опрос НЕ идёт: там никто не смотрит на счёт, а за
 * эндпоинтом стоят два запроса к базе. Возврат во вкладку перечитает сам.
 */
export function pnlKey(conditionID: string | undefined) {
  return ["market-pnl", conditionID ?? ""] as const;
}

const POLL_MS = 3000;

export function useMarketPnl(conditionID: string | undefined) {
  return useQuery<MarketPnlResponse>({
    queryKey: pnlKey(conditionID),
    queryFn: () => api.marketPnl(conditionID ?? ""),
    enabled: Boolean(conditionID),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    // Счёт устаревает сам по себе — вместе с каждой сделкой и каждым тиком
    // резолюции; держать его «свежим» между перерисовками бессмысленно.
    staleTime: 0,
  });
}

/**
 * Просит немедленно перечитать счёт.
 *
 * Нужен после НАШЕГО нажатия: пробный налив ложится в журнал за миллисекунды, и
 * предлагать оператору смотреть на неизменившуюся позицию до следующего тика
 * опроса нельзя. Настоящий филл приезжает сюда тем же путём — через реестр
 * рефрешеров в store/dispatch.ts.
 */
export function useRefreshPnl(conditionID: string | undefined): () => void {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: pnlKey(conditionID) });
}
